import { FilterField } from '@/users/pages/UserFilters/types/FilterField';

export const sortFolders = (
  folders: any[],
  filters: {
    [key: string]: any;
  },
  customFields: FilterField[]
) => {
  // Find which fields have sort enabled
  const sortFields = customFields
    .map((field) => ({
      ...field,
      sortDirection: filters[`${field.name}_sort`],
    }))
    .filter((field) => field.sortDirection);

  if (sortFields.length === 0) {
    return folders;
  }

  // Helper to extract nested value using path
  const extractValue = (value: any, path: string): any => {
    if (!path || path === '') return value;
    const pathParts = path.split('.');
    let result = value;
    for (const part of pathParts) {
      if (result && typeof result === 'object' && part in result) {
        result = result[part];
      } else {
        return null;
      }
    }
    return result;
  };

  return [...folders].sort((a, b) => {
    for (const sortField of sortFields) {
      // Get field value from first file that has it
      const getFieldValue = (folder: any) => {
        for (const file of folder.files || []) {
          // Split name by '.' and use left part for all field types
          let fieldNameToFind = sortField.name;
          if (sortField.name.includes('.')) {
            fieldNameToFind = sortField.name.split('.')[0];
          }

          const field = file.fields?.find(
            (f: any) => f.name === fieldNameToFind
          );
          if (field && field.value) {
            const value = extractValue(field.value, sortField.path);
            return value ? value.toString().toLowerCase() : '';
          }
        }
        return '';
      };

      const aValue = getFieldValue(a);
      const bValue = getFieldValue(b);

      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      if (comparison !== 0) {
        return sortField.sortDirection === 'asc' ? comparison : -comparison;
      }
    }
    return 0;
  });
};

export const filterFolders = (
  folders: any[],
  filters: {
    isArchived: boolean;
    onlyWithData: boolean;
    backOfficeStatus: string;
    folderName: string;
    fileName: string;
    fileType: string;
    isPublished: string;
    isSigned: string;
    isLocked: string;
    fileStatus: string;
    [key: string]: any;
  },
  customFields: FilterField[]
) => {
  // Helper to extract nested value using path
  const extractValue = (value: any, path: string): any => {
    if (!path || path === '') return value;
    const pathParts = path.split('.');
    let result = value;
    for (const part of pathParts) {
      if (result && typeof result === 'object' && part in result) {
        result = result[part];
      } else {
        return null;
      }
    }
    return result;
  };
  return folders.filter((folder) => {
    // Filter by archived status (toggle between archived and non-archived)
    if (filters.isArchived) {
      // Show only archived folders
      if (!folder.isArchived) {
        return false;
      }
    } else {
      // Show only non-archived folders
      if (folder.isArchived) {
        return false;
      }
    }

    // Filter by back office status
    if (filters.backOfficeStatus !== '') {
      if (folder.backOfficeStatus?.toString() !== filters.backOfficeStatus) {
        return false;
      }
    }

    // Filter by folderName
    if (filters.folderName && filters.folderName.trim() !== '') {
      const folderNameFilter = filters.folderName.trim().toUpperCase();
      if (
        !folder.folderName ||
        !folder.folderName.toUpperCase().includes(folderNameFilter)
      ) {
        return false;
      }
    }

    // Combine all file-related filters to check if there's a file matching ALL conditions
    // This ensures that when filtering by fileType and isSigned, we find files that match BOTH
    const hasFileMatchingAllFilters = folder.files?.some((file: any) => {
      // Check fileName filter
      if (filters.fileName && filters.fileName.trim() !== '') {
        const fileNameFilter = filters.fileName.trim().toUpperCase();
        if (!file.name || !file.name.toUpperCase().includes(fileNameFilter)) {
          return false;
        }
      }

      // Check fileType filter
      if (filters.fileType && filters.fileType !== '') {
        if (file.type !== filters.fileType) {
          return false;
        }
      }

      // Check isPublished filter
      // Note: filters.isPublished is a string ('true'/'false'), but file.isPublished is a boolean
      if (filters.isPublished !== '') {
        const isPublishedFilter = filters.isPublished === 'true'; // Convert string to boolean
        if (
          !file.hasOwnProperty('isPublished') ||
          file.isPublished !== isPublishedFilter
        ) {
          return false;
        }
      }

      // Check isSigned filter
      // Note: filters.isSigned is a string ('true'/'false'), but file.isSigned is a boolean
      if (filters.isSigned !== '') {
        const isSignedFilter = filters.isSigned === 'true'; // Convert string to boolean
        if (
          !file.hasOwnProperty('isSigned') ||
          file.isSigned !== isSignedFilter
        ) {
          return false;
        }
      }

      // Check isLocked filter
      // Note: filters.isLocked is a string ('true'/'false'), but file.isLocked is a boolean
      if (filters.isLocked !== '') {
        const isLockedFilter = filters.isLocked === 'true'; // Convert string to boolean
        if (
          !file.hasOwnProperty('isLocked') ||
          file.isLocked !== isLockedFilter
        ) {
          return false;
        }
      }

      // Check fileStatus filter
      if (filters.fileStatus !== '') {
        const statusFilter = parseInt(filters.fileStatus, 10);
        if (!file.hasOwnProperty('status') || file.status !== statusFilter) {
          return false;
        }
      }

      // If we get here, the file matches all file-related filter conditions
      return true;
    });

    // If any file-related filters are active, check if we found a matching file
    const hasFileFilters =
      (filters.fileName && filters.fileName.trim() !== '') ||
      (filters.fileType && filters.fileType !== '') ||
      filters.isPublished !== '' ||
      filters.isSigned !== '' ||
      filters.isLocked !== '' ||
      filters.fileStatus !== '';

    if (hasFileFilters && !hasFileMatchingAllFilters) {
      return false;
    }

    // Filter by onlyWithData - check if folder has data in at least one custom field
    if (filters.onlyWithData) {
      // Check if folder has data for at least ONE custom field (any field, not just filtered ones)
      const hasAtLeastOneField = customFields.some((customField) => {
        // Check if any file in the folder has this field with a non-empty value
        return folder.files?.some((file: any) => {
          // Split name by '.' and use left part for all field types
          let fieldNameToFind = customField.name;
          if (customField.name.includes('.')) {
            fieldNameToFind = customField.name.split('.')[0];
          }

          const field = file.fields?.find(
            (f: any) => f.name === fieldNameToFind
          );
          if (field && field.value) {
            const value = extractValue(field.value, customField.path);
            return value && value.toString().trim() !== '';
          }
          return false;
        });
      });

      if (!hasAtLeastOneField) {
        return false;
      }
    }

    // Filter by custom fields
    for (const customField of customFields) {
      const filterValue = filters[customField.name];

      // Handle string-based filtering
      if (
        filterValue &&
        typeof filterValue === 'string' &&
        filterValue.trim() !== ''
      ) {
        // Split name by '.' and use left part for all field types
        let fieldNameToFind = customField.name;
        if (customField.name.includes('.')) {
          fieldNameToFind = customField.name.split('.')[0];
        }

        // Check if folder has any file with this field matching the value
        const hasMatch = folder.files?.some((file: any) => {
          return file.fields?.some((field: any) => {
            if (field.name === fieldNameToFind && field.value) {
              const value = extractValue(field.value, customField.path);

              // Handle checkboxGroup fields where value is an array
              if (
                customField.type === 'checkboxGroup' &&
                Array.isArray(value)
              ) {
                // Check if any item in the array matches the filter value
                return value.some((item: any) =>
                  item
                    ?.toString()
                    .toLowerCase()
                    .includes(filterValue.toLowerCase())
                );
              }

              // Handle other field types (string, number, etc.)
              return (
                value &&
                value
                  .toString()
                  .toLowerCase()
                  .includes(filterValue.toLowerCase())
              );
            }
            return false;
          });
        });

        if (!hasMatch) {
          return false;
        }
      }
    }

    return true;
  });
};

export const paginateFolders = (
  folders: any[],
  currentPage: number,
  itemsPerPage: number
) => {
  // Validate itemsPerPage to prevent division by zero
  const validItemsPerPage = Math.max(1, itemsPerPage || 10);

  const totalItems = folders.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / validItemsPerPage));

  // Clamp currentPage to valid range
  const validPage = Math.max(1, Math.min(currentPage, totalPages));

  const startIndex = (validPage - 1) * validItemsPerPage;
  const endIndex = startIndex + validItemsPerPage;
  const paginatedFolders = folders.slice(startIndex, endIndex);

  return {
    paginatedFolders,
    totalItems,
    totalPages,
    startIndex,
    endIndex,
  };
};

// Helper functions for localStorage
export const getFoldersState = (filterId?: string) => {
  const key = filterId ? `userFoldersState_${filterId}` : 'userFoldersState';
  const stored = localStorage.getItem(key);

  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const setFoldersState = (
  state: {
    currentPage: number;
    itemsPerPage: number;
    filters: {
      isArchived: boolean;
      onlyWithData: boolean;
      backOfficeStatus: string;
      folderName: string;
      isPublished: string;
      isSigned: string;
      isLocked: string;
      fileStatus: string;
      [key: string]: any;
    };
  },
  filterId?: string
) => {
  const key = filterId ? `userFoldersState_${filterId}` : 'userFoldersState';
  localStorage.setItem(key, JSON.stringify(state));
};
